import log from 'loglevel';

import { Folder, HierarchicalFolder } from '../models';
import { createApiWithAuth } from '../utils/axios';
import { LabelAndFolderReqType } from '../utils/constants';

interface ApiResponse<T> {
  message: T;
}

type CreateFolderResponse = ApiResponse<Folder>;
type UpdateFolderResponse = ApiResponse<Folder>;
type DeleteFolderResponse = ApiResponse<{ success: boolean }>;

let outlookFolderApi: ReturnType<typeof createApiWithAuth> | null = null;

const getOutlookFolderApi = () => {
  if (!outlookFolderApi) {
    const baseUrl = import.meta.env.VITE_MICROSOFT_BASE_URL;
    if (!baseUrl) {
      throw new Error('Outlook Folders Service URL is not set');
    }
    outlookFolderApi = createApiWithAuth(baseUrl);
  }
  return outlookFolderApi;
};

const handleRequestError = (context: string, error: unknown) => {
  log.error(`Outlook Folder API Error [${context}]:`, error);
  throw error;
};

interface DeleteFolderParams {
  platformAccountId: string;
  labelId: string;
}

interface UpdateFolderParams {
  labelId: string;
  platformAccountId: string;
  name?: string;
  signal?: AbortSignal;
}

interface CreateFolderParams {
  name: string;
  platformAccountId: string;
  reqType: LabelAndFolderReqType;
  parentName?: string;
}

const getUserFolders = async (): Promise<HierarchicalFolder[]> => {
  try {
    const { data } = await getOutlookFolderApi().get('/users/folders');

    return (data?.message ?? []) as HierarchicalFolder[];
  } catch (error) {
    log.error('Outlook Folder API Error [getUserFolders]:', error);
    return [];
  }
};

const deleteFolder = async (
  params: DeleteFolderParams,
): Promise<DeleteFolderResponse> => {
  try {
    const { data } = await getOutlookFolderApi().post<DeleteFolderResponse>(
      '/folder/delete',
      params,
    );

    return data;
  } catch (error) {
    handleRequestError('deleteFolder', error);
    throw error;
  }
};

const updateFolder = async (
  params: UpdateFolderParams,
): Promise<UpdateFolderResponse> => {
  try {
    const { signal, ...requestParams } = params;
    const { data } = await getOutlookFolderApi().post<UpdateFolderResponse>(
      '/folder/update',
      requestParams,
      {
        signal,
      },
    );

    return data;
  } catch (error) {
    handleRequestError('updateFolder', error);
    throw error;
  }
};

const createFolder = async (
  params: CreateFolderParams,
): Promise<CreateFolderResponse> => {
  try {
    const { data } = await getOutlookFolderApi().post<CreateFolderResponse>(
      '/folder/create',
      params,
    );

    return data;
  } catch (error) {
    handleRequestError('createFolder', error);
    throw error;
  }
};

export { createFolder, deleteFolder, getUserFolders, updateFolder };
